import {Header} from '@tanstack/react-table'
import {
    StyledTableWrapper,
    StyledTableBodyCell,
    StyledTableBodyRow,
    StyledTableColumnResizer,
    StyledTableHeader,
    StyledTableHeaderCell,
    StyledTableHeaderCellLabel,
    StyledTableContainerHorizontalScrollbar,
    StyledTableContainerVerticalScrollbar,
    StyledTablePagination,
    StyledTablePaginationLimit,
    StyledTableContainer,
    StyledTableContainerScrollbarCorner,
    StyledBodyCellText
} from '@components/ui/table-atoms/style.ts'
import {Button} from '@components/ui/button/Button.tsx'
import {
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronSelectorVerticalIcon,
    ChevronUpIcon
} from '@components/ui/icon/Icon.tsx'
import {TableProps} from '@components/commons/table/Table.tsx'
import {FC, forwardRef, HTMLAttributes, ReactNode} from 'react'
import * as RadixScrollArea from '@radix-ui/react-scroll-area'
import {Trans} from '@/translations/i18n.tsx'
import {SelectProvider} from '@ariakit/react'
import {SelectItem, SelectList, SelectPopover, SelectTrigger} from '@components/ui/select-atoms/SelectAtoms.tsx'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'

/**
 * This component is the main parent of the table
 * */
export const TableWrapper = (props: HTMLAttributes<HTMLDivElement>) => (
    <StyledTableWrapper {...props}>{props.children}</StyledTableWrapper>
)

/**
 * This component is the "window" where the subcomponents live and handles scrolls
 * */
export const TableContainer = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement> & {hasPagination: boolean}>(
    ({children, hasPagination, ...rest}, ref) => (
        <StyledTableContainer {...rest} ref={ref}>
            <RadixScrollArea.ScrollArea style={{height: '100%'}}>
                <RadixScrollArea.Viewport style={{height: '100%'}}>
                    {children}
                    <StyledTableContainerVerticalScrollbar orientation="vertical" />
                    <StyledTableContainerHorizontalScrollbar $hasPagination={hasPagination} orientation="horizontal" />
                    <StyledTableContainerScrollbarCorner />
                </RadixScrollArea.Viewport>
            </RadixScrollArea.ScrollArea>
        </StyledTableContainer>
    )
)

/**
 * This component is a simple header row that wraps the header cells
 * */
export const TableHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>((props, ref) => (
    <StyledTableHeader {...props} ref={ref} />
))

interface TableHeaderCellProps extends HTMLAttributes<HTMLDivElement> {
    cellSize: number
    isPinned: boolean
}
/**
 * This component is a complex header cell that contains the passed children, also it manages the cell size and the sorting
 * */
export const TableHeaderCell: FC<TableHeaderCellProps> = ({cellSize, isPinned, ...rest}) => (
    <StyledTableHeaderCell $isPinned={isPinned} {...rest} style={{width: cellSize, ...rest.style}} />
)

/**
 * This component is a simple styled label for the header cell
 * */
export const TableHeaderCellLabel = (props: HTMLAttributes<HTMLDivElement>) => <StyledTableHeaderCellLabel {...props} />

interface TableBodyProps<TData extends unknown[]> extends HTMLAttributes<HTMLDivElement> {
    data: TData
    rowRender: (item: TData[number], index: number) => ReactNode
}
/**
 * This component is a container that renders the rows as children
 * */
export const TableBody = <TData extends unknown[]>({data, rowRender, style, ...rest}: TableBodyProps<TData>) => (
    <div style={{isolation: 'isolate', ...style}} {...rest}>
        {data.map((value, index) => rowRender(value, index))}
    </div>
)

export interface TableBodyRowProps extends HTMLAttributes<HTMLDivElement> {
    isSelected: boolean
}
/**
 * This component is a simple styled body row that wraps the cells
 * */
export const TableBodyRow: FC<TableBodyRowProps> = ({isSelected, ...rest}) => (
    <StyledTableBodyRow $isSelected={isSelected} {...rest} />
)

export interface TableBodyCellProps extends HTMLAttributes<HTMLDivElement> {
    cellSize: number
    isSelected?: boolean
    isPinned?: boolean
}
/**
 * This component is a simple styled cell that reacts to the cell size
 * */
export const TableBodyCell: FC<TableBodyCellProps> = ({cellSize, isPinned, isSelected, ...rest}) => (
    <StyledTableBodyCell
        $isSelected={isSelected}
        $isPinned={isPinned}
        {...rest}
        style={{width: cellSize, ...rest.style}}
    />
)

/**
 * This component is a simple styled cell text
 * */
interface TableBodyCellTextProps extends HTMLAttributes<HTMLParagraphElement> {
    variant?: 'light' | 'normal' | 'dark' | 'danger'
    weight?: 'light' | 'regular' | 'bold'
}
export const TableBodyCellText = forwardRef<HTMLParagraphElement, TableBodyCellTextProps>(
    ({variant, weight, ...rest}, ref) => <StyledBodyCellText $variant={variant} $weight={weight} ref={ref} {...rest} />
)

/**
 * This component is a simple styled element that set the cell size
 * */
export const TableColumnResizer = ({header}: {header: Header<unknown, unknown>}) => (
    <StyledTableColumnResizer
        className="column-resizer"
        onDoubleClick={() => header.column.resetSize()}
        onMouseDown={header.getResizeHandler()}
        onTouchStart={header.getResizeHandler()}
    />
)

/**
 * This component is a button that set the sorter
 * */
export const TableSortButton = <TData extends unknown[], TSortName extends string>({
    sortName,
    sorter
}: {
    sortName: TSortName
    sorter: NonNullable<TableProps<TData, TSortName>['sorter']>
}) => {
    if (sortName != sorter.sortName) {
        return (
            <Button size="xs" shape="square" variant="ghost" onClick={() => sorter.onSort(sortName, 'desc')}>
                <ChevronSelectorVerticalIcon />
            </Button>
        )
    }
    if (sorter.direction == 'desc') {
        return (
            <Button size="xs" shape="square" variant="ghost" onClick={() => sorter.onSort(sortName, 'asc')}>
                <ChevronDownIcon />
            </Button>
        )
    }

    return (
        <Button size="xs" shape="square" variant="ghost" onClick={() => sorter.onSort(null, null)}>
            <ChevronUpIcon />
        </Button>
    )
}

export interface TablePaginationProps {
    pagination: {
        limit: number
        currentPage: number
        total: number
        onChangeLimit: (limit: number) => void
        onChangePage: (page: number) => void
    }
}

const getPages = (totalPages: number, currentPage: number): Array<number | null> => {
    if (totalPages <= 7) {
        return Array.from({length: totalPages}).map((_, index) => index + 1)
    }
    if (currentPage <= 4) {
        return [...Array.from({length: 5}).map((_, index) => index + 1), null, totalPages]
    }
    if (currentPage >= totalPages - 3) {
        return [1, null, ...Array.from({length: 5}).map((_, index) => totalPages - 4 + index)]
    } else {
        return [1, null, currentPage - 1, currentPage, currentPage + 1, null, totalPages]
    }
}
/**
 * This component is the pagination bar
 * */
export const TablePagination: FC<TablePaginationProps> = ({pagination}) => {
    // const {t} = useTranslation()
    const lastPage = pagination.total >= pagination.limit ? Math.ceil(pagination.total / pagination.limit) : 1
    const pageNumbers = getPages(lastPage, pagination.currentPage)
    // const [goToPageSearch, setGoToPageSearch] = useState('')

    return (
        <StyledTablePagination>
            <StyledTablePaginationLimit>
                <Trans
                    i18nKey="commons:show_x_of_y_rows"
                    components={{
                        number: (
                            <SelectProvider
                                placement="top"
                                value={pagination.limit.toString()}
                                setValue={value => pagination.onChangeLimit(Number(value))}
                            >
                                <SelectTrigger value={pagination.limit.toString()} displayValue={value => value} />
                                <SelectPopover portal sameWidth={false}>
                                    <SelectList>
                                        <SelectItem hasItemCheckbox value="20">
                                            20
                                        </SelectItem>
                                        <SelectItem hasItemCheckbox value="50">
                                            50
                                        </SelectItem>
                                        <SelectItem hasItemCheckbox value="100">
                                            100
                                        </SelectItem>
                                    </SelectList>
                                </SelectPopover>
                            </SelectProvider>
                        ),
                        b: <b />
                    }}
                    values={{count: pagination.total}}
                />
            </StyledTablePaginationLimit>
            <Flexbox gap={0.5} align="center" justify="center">
                <Button
                    disabled={pagination.currentPage == 1}
                    shape="square"
                    size="xs"
                    variant="ghost"
                    onClick={() => pagination.onChangePage(pagination.currentPage - 1)}
                >
                    <ChevronLeftIcon />
                </Button>
                {pageNumbers.map((pageNumber, index) => (
                    <Button
                        style={{whiteSpace: 'nowrap', pointerEvents: pageNumber ? undefined : 'none'}}
                        shape={!pageNumber || pageNumber < 9 ? 'square' : 'rectangle'}
                        size="xs"
                        variant={pageNumber == pagination.currentPage ? 'secondary' : 'ghost'}
                        onClick={pageNumber ? () => pagination.onChangePage(pageNumber) : undefined}
                        key={index}
                    >
                        {pageNumber ?? '...'}
                    </Button>
                ))}
                <Button
                    disabled={pagination.currentPage == lastPage}
                    shape="square"
                    size="xs"
                    variant="ghost"
                    onClick={() => pagination.onChangePage(pagination.currentPage + 1)}
                >
                    <ChevronRightIcon />
                </Button>
            </Flexbox>
            {/* <ComboboxProvider resetValueOnHide setValue={setGoToPageSearch}>
                <SelectProvider
                    placement="top"
                    value={pagination.currentPage.toString()}
                    setValue={value => pagination.onChangePage(Number(value))}
                >
                    <SelectTrigger
                        value={pagination.currentPage.toString()}
                        displayValue={() => t('commons:go_to_page')}
                        style={{justifySelf: 'end'}}
                    />
                    <SelectComboboxPopover portal sameWidth>
                        <SelectComboboxInput autoSelect />
                        <SelectComboboxList>
                            {Array.from({length: lastPage})
                                .map((_, index) => (
                                    <SelectItem
                                        hasItemCheckbox
                                        value={(index + 1).toString()}
                                        render={<ComboboxItem />}
                                        key={index + 1}
                                    >
                                        {index + 1}
                                    </SelectItem>
                                ))
                                .filter((_, index) => (index + 1).toString().startsWith(goToPageSearch))}
                        </SelectComboboxList>
                    </SelectComboboxPopover>
                </SelectProvider>
            </ComboboxProvider> */}
        </StyledTablePagination>
    )
}
